import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "stores/auth/auth-context";
import { Modal } from "design-system/components/practice/modal/modal";
import {
  getLeaderboardData,
  getPracticeStatus
} from "services/api/practice/practiceService";
import { getSubscriptionStatus } from "services/api/sessions/sessionsService";
import type {
  PracticeStatus,
  PracticeLeaderboardData
} from "services/api/practice/practiceService.types";
import { SubscriptionType } from "services/api/sessions/sessionService.types";
import { PracticeCard } from "../practice-card/practice-card";
import {
  PracticeHistory,
  PracticeHistoryViewState
} from "../practice-history/practice-history";
import { PracticeSubscriptionPaused } from "../practice-subscription-paused/practice-subscription-paused";
import { PracticeLeaderboard } from "../practice-leaderboard/practice-leaderboard";
import { PracticeErrorScreen } from "../practice-error/practice-error";
import {
  trackPracticeContinue,
  trackPracticeError,
  trackPracticeHistoryViewed,
  trackPracticeScreenViewed,
  trackPracticeStarted
} from "services/api/app-events/practiceEvents";
import {
  PracticeWrapper,
  Divider,
  PracticeOuterWrapper
} from "./practice-main-screen.styles";

export const PracticeMainScreen: React.FC = () => {
  const router = useRouter();
  const { user } = useAuth();
  const accessToken = user?.userData?.primary_user?.access_token ?? null;

  const [practiceStatus, setPracticeStatus] = useState<PracticeStatus | null>(
    null
  );
  const [weeklyData, setWeeklyData] = useState<PracticeLeaderboardData | null>(
    null
  );
  const [semesterlyData, setSemesterlyData] =
    useState<PracticeLeaderboardData | null>(null);
  const [error, setError] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyViewState, setHistoryViewState] =
    useState<PracticeHistoryViewState>("loading");
  const [isSubscriptionPausedOpen, setIsSubscriptionPausedOpen] =
    useState(false);
  const requestIdRef = useRef(0);
  useEffect(() => {
    if (!accessToken) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        trackPracticeScreenViewed(accessToken);
      }
    };

    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [accessToken]);

  const fetchData = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    setError(false);
    setPracticeStatus(null);
    setWeeklyData(null);
    setSemesterlyData(null);

    if (!accessToken) return;

    try {
      const [statusResponse, weeklyResponse, semesterlyResponse] =
        await Promise.all([
          getPracticeStatus(accessToken),
          getLeaderboardData(accessToken, "weekly"),
          getLeaderboardData(accessToken, "semesterly")
        ]);

      if (requestId !== requestIdRef.current) return;

      setPracticeStatus(statusResponse.data.status);
      setWeeklyData(weeklyResponse.data);
      setSemesterlyData(semesterlyResponse.data);
    } catch (requestError) {
      if (requestId !== requestIdRef.current) return;

      Sentry.captureException(requestError, {
        tags: { screen: "PracticeMainScreen", operation: "load" }
      });
      trackPracticeError(
        accessToken,
        "PracticeMainScreen",
        requestError instanceof Error
          ? requestError.message
          : String(requestError),
        "load"
      );
      setError(true);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchData();

    return () => {
      requestIdRef.current += 1;
    };
  }, [fetchData]);

  useEffect(() => {
    if (!router.isReady || router.query.subscriptionPaused !== "true") return;

    setIsSubscriptionPausedOpen(true);

    const query = { ...router.query };
    delete query.subscriptionPaused;
    void router.replace({ pathname: "/latihan", query }, undefined, {
      shallow: true
    });
  }, [router, router.isReady, router.query.subscriptionPaused]);

  const handleStart = async () => {
    if (!accessToken) return;

    if (practiceStatus === "not_started") {
      trackPracticeStarted(accessToken);
    } else if (practiceStatus === "ongoing") {
      trackPracticeContinue(accessToken);
    }

    try {
      const { data } = await getSubscriptionStatus(accessToken);
      if (
        data.subscriptionStatus.subscriptionType === SubscriptionType.PAUSED
      ) {
        setIsSubscriptionPausedOpen(true);
        return;
      }
    } catch (requestError) {
      Sentry.captureException(requestError, {
        tags: { screen: "PracticeMainScreen", operation: "check-subscription" }
      });
      trackPracticeError(
        accessToken,
        "PracticeMainScreen",
        requestError instanceof Error
          ? requestError.message
          : String(requestError),
        "check-subscription"
      );
      setIsSubscriptionPausedOpen(true);
      return;
    }

    router.push({
      pathname: "/latihan/session",
      query: practiceStatus === "not_started" ? { shouldStart: "true" } : {}
    });
  };

  if (error) {
    return (
      <PracticeOuterWrapper>
        <PracticeErrorScreen
          onRetry={fetchData}
          onContactSupport={() => router.push("/hotline-kakaksiaga")}
        />
      </PracticeOuterWrapper>
    );
  }

  return (
    <PracticeOuterWrapper>
      <PracticeWrapper>
        <PracticeCard
          practiceStatus={practiceStatus}
          handleStartSession={handleStart}
          onOpenHistory={() => {
            trackPracticeHistoryViewed(
              accessToken,
              practiceStatus ?? "unknown"
            );
            setHistoryViewState("loading");
            setIsHistoryOpen(true);
          }}
        />
        <Divider />
        <PracticeLeaderboard
          accessToken={accessToken}
          weeklyData={weeklyData}
          semesterlyData={semesterlyData}
        />
      </PracticeWrapper>
      <Modal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        closeIcon
        closeOnOutsideClick={false}
        heading={historyViewState === "list" ? "Riwayat Latihan" : undefined}
        size={historyViewState === "list" ? "lg" : "sm"}
      >
        {isHistoryOpen && (
          <PracticeHistory
            accessToken={accessToken ?? ""}
            onClose={() => setIsHistoryOpen(false)}
            onStartPractice={handleStart}
            onSelectHistory={(historyId) =>
              router.push(`/latihan/riwayat/${historyId}`)
            }
            onViewStateChange={setHistoryViewState}
          />
        )}
      </Modal>
      <Modal
        isOpen={isSubscriptionPausedOpen}
        onClose={() => setIsSubscriptionPausedOpen(false)}
        closeIcon
        closeOnOutsideClick={false}
        size="sm"
      >
        <PracticeSubscriptionPaused
          onClose={() => setIsSubscriptionPausedOpen(false)}
        />
      </Modal>
    </PracticeOuterWrapper>
  );
};
